import * as React from 'react';
import { LayoutList, Search, Table2 } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  listBandActionRecipe,
  listBandBarRecipe,
  listBandCountRecipe,
  listBandRecipe,
  listFootRecipe,
  listToolbarRecipe,
  listToolbarSearchRecipe,
  type ListBandBarRecipeProps,
  type ListFootRecipeProps,
} from '../recipes/list.recipe';
import { Button } from './Button';
import { Input } from './Input';
import { Inline, Stack } from './Layout';
import { Text } from './Text';
import { ToggleGroup, ToggleGroupItem } from './ToggleGroup';

/*
 * THE GROUPED LIST COLUMN
 *
 * The pieces of the 400-wide list beside a record: the toolbar over it, the
 * band over each group, and the strip under the rows. The rows themselves are
 * `ListRow shape="band"`. Each piece carries the column's inset, so the
 * column is flush and a selected row can colour its full width.
 */

export interface ListBandProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    ListBandBarRecipeProps {
  label: React.ReactNode;
  count: React.ReactNode;
  /**
   * A control at the far end of the band. The reports list groups rows and
   * has nothing to put here; the checklists list is one band and hangs its
   * Add on it, rather than a second toolbar above a list of five rows.
   */
  action?: React.ReactNode;
}

/**
 * The band over a group of rows: what the group is, and how many are in it.
 *
 *   <ListBand label="Issues found" count={3} tone="destructive" />
 *
 * `tone` follows the badge tone of what the group is, so a red band sits
 * over red badges. A group that is not a severity — a truck type, a driver —
 * is `neutral`, so the reds and greens keep meaning something.
 */
export const ListBand = React.forwardRef<HTMLDivElement, ListBandProps>(
  function ListBand({ label, count, tone, action, className, ...props }, ref) {
    return (
      <div ref={ref} className={cn(listBandRecipe(), className)} {...props}>
        <div className={listBandBarRecipe({ tone })}>
          <Text variant="label" truncate>
            {label}
          </Text>
          <Text variant="label" className={listBandCountRecipe()}>
            {count}
          </Text>
          {action ? (
            <span className={listBandActionRecipe()}>{action}</span>
          ) : null}
        </div>
      </div>
    );
  }
);

export interface ListFootProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ListFootRecipeProps {}

/**
 * The strip under a list's rows, at the column's inset — a link that reveals
 * the archived rows, say. `RangePager` is this with a pager in it.
 */
export const ListFoot = React.forwardRef<HTMLDivElement, ListFootProps>(
  function ListFoot({ density, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(listFootRecipe({ density }), className)}
        {...props}
      />
    );
  }
);

export interface RangePagerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** One-based. */
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/**
 * "Showing 1 to 100 of 250" with Previous and Next, under a list that pages
 * rather than numbers its pages.
 *
 *   <RangePager page={meta.pageNo} pageSize={meta.pageSize}
 *     total={meta.totalCount} totalPages={pageCount(meta)}
 *     onPageChange={setPage} />
 *
 * `meta` and `pageCount` are the API's, from @relay-towing/api.
 */
export const RangePager = React.forwardRef<HTMLDivElement, RangePagerProps>(
  function RangePager(
    { page, pageSize, total, totalPages, onPageChange, ...props },
    ref
  ) {
    return (
      <ListFoot ref={ref} {...props}>
        <Text variant="caption" tone="muted">
          Showing {(page - 1) * pageSize + 1} to{' '}
          {Math.min(page * pageSize, total)} of {total}
        </Text>
        <Inline gap={2}>
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </Inline>
      </ListFoot>
    );
  }
);

/** How the left column is drawn: grouped rows, or the wide table. */
export type ListMode = 'list' | 'table';

export interface ListToolbarProps {
  mode: ListMode;
  onModeChange: (mode: ListMode) => void;
  search: string;
  onSearchChange: (search: string) => void;
  /** What the box says before anyone types, e.g. "Search by truck or name". */
  searchPlaceholder: string;
  /** What a screen reader hears on the magnifier and the box. */
  searchLabel: string;
  /** Grouping, Filters: whatever sits between the switch and the magnifier. */
  children?: React.ReactNode;
}

/**
 * The row above a grouped list: the List / Table switch, the page's own
 * buttons (`Button size="compact"`), and a magnifier that reveals the search
 * box.
 *
 *   <ListToolbar mode={mode} onModeChange={setMode} search={search}
 *     onSearchChange={setSearch} searchPlaceholder="Search by truck or name"
 *     searchLabel="Search the reports already loaded">
 *     <Button variant="ghost" size="compact"><Filter />Filters</Button>
 *   </ListToolbar>
 *
 * The box only appears when it is asked for. A permanent one cost a whole
 * row of the column to a field almost nobody types in, and the column is 400
 * wide. The value belongs to the page rather than to this, because one list
 * searches what it has already loaded and the other sends the word to the
 * server.
 */
export function ListToolbar({
  mode,
  onModeChange,
  search,
  onSearchChange,
  searchPlaceholder,
  searchLabel,
  children,
}: ListToolbarProps) {
  const [searching, setSearching] = React.useState(false);
  const searchRef = React.useRef<HTMLInputElement>(null);

  // Revealing the box and then making somebody click it is half a feature.
  React.useEffect(() => {
    if (searching) searchRef.current?.focus();
  }, [searching]);

  return (
    <Stack gap={0}>
      <div className={listToolbarRecipe()}>
        <ToggleGroup
          type="single"
          size="compact"
          variant="plain"
          bordered={false}
          value={mode}
          onValueChange={(next) => next && onModeChange(next as ListMode)}
        >
          <ToggleGroupItem value="table">
            <Table2 />
            Table
          </ToggleGroupItem>
          <ToggleGroupItem value="list">
            <LayoutList />
            List
          </ToggleGroupItem>
        </ToggleGroup>

        <Inline gap={1} align="center">
          {children}

          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={searchLabel}
            aria-expanded={searching}
            onClick={() => setSearching((open) => !open)}
          >
            <Search />
          </Button>
        </Inline>
      </div>

      {searching && (
        <div className={listToolbarSearchRecipe()}>
          <Input
            ref={searchRef}
            size="sm"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Escape') return;
              onSearchChange('');
              setSearching(false);
            }}
            onBlur={() => {
              if (!search) setSearching(false);
            }}
            placeholder={searchPlaceholder}
            aria-label={searchLabel}
            leadingIcon={<Search />}
          />
        </div>
      )}
    </Stack>
  );
}
